import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { useState, useEffect } from "react";
import React from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useLazyGetActivityLogListQuery } from "../../services/activityLog";
import { showError } from "../../helpers";
import { ACTIVITY_MODULES, ACTIVITY_TYPES } from "../../constants/enums";
import Pagination from "../../components/Panigation";

const ManageActivityLogs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [UserRole, setUserRole] = React.useState("default");
  const handleUserRoleChange = (event: SelectChangeEvent) => {
    setUserRole(event.target.value as string);
  };
  const [ActionType, setActionType] = React.useState("default");
  const handleActionTypeChange = (event: SelectChangeEvent) => {
    setActionType(event.target.value as string);
  };

  // Interface for activity log data from API
  interface ActivityLogUser {
    _id: string;
    email?: string;
    userType?: number;
  }

  interface ActivityLogData {
    _id: string;
    userId: ActivityLogUser;
    ipAddress?: string;
    userType: number;
    module: number;
    activityType: number;
    details: string;
    metaData?: any;
    hiddenFromUser: boolean;
    createdBy: string;
    isSystem: boolean;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  }

  interface ActivityLogsResponse {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    logs: ActivityLogData[];
  }

  interface ApiResponse {
    statusCode: number;
    message: string;
    data: ActivityLogsResponse;
    status: number;
  }

  // State for activity logs data
  const [activityLogs, setActivityLogs] = useState<ActivityLogData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [getLogs] = useLazyGetActivityLogListQuery();

  const formatEnumLabel = (key: string): string =>
    key
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // Helper function to get module name from enum
  const getModuleName = (moduleValue: number): string => {
    const moduleKey = Object.keys(ACTIVITY_MODULES).find(
      (key) =>
        ACTIVITY_MODULES[key as keyof typeof ACTIVITY_MODULES] === moduleValue
    );
    return moduleKey ? formatEnumLabel(moduleKey) : `Module ${moduleValue}`;
  };

  // Helper function to get activity type name from enum
  const getActivityTypeName = (activityTypeValue: number): string => {
    const activityKey = Object.keys(ACTIVITY_TYPES).find(
      (key) =>
        ACTIVITY_TYPES[key as keyof typeof ACTIVITY_TYPES] === activityTypeValue
    );
    return activityKey
      ? formatEnumLabel(activityKey)
      : `Activity ${activityTypeValue}`;
  };

  // Format date for display
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchLogsList = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: page,
        limit: pageSize,
        search: debouncedSearchTerm,
        role: "user",
      };
      const response = (await getLogs(params).unwrap()) as ApiResponse;
      if (response?.statusCode === 200 && response?.data) {
        setActivityLogs(response.data.logs || []);
        setTotalPages(response.data.totalPages || 0);
        setTotal(response.data.total || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch Logs");
      setActivityLogs([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchLogsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearchTerm]);
  return (
    <>
      <div className="main_title">
        <h1>Activity Log</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Activity Log
        </p>
      </div>
      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              placeholder="Search..."
            />
            <div className="form">
              <Select
                fullWidth
                value={UserRole}
                onChange={handleUserRoleChange}
              >
                <MenuItem disabled value="default">
                  User Role
                </MenuItem>
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Subadmin">Subadmin</MenuItem>
                <MenuItem value="Vendor">Vendor</MenuItem>
                <MenuItem value="Customer">Customer</MenuItem>
              </Select>
              <Select
                fullWidth
                value={ActionType}
                onChange={handleActionTypeChange}
              >
                <MenuItem disabled value="default">
                  Action Type
                </MenuItem>
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Login">Login</MenuItem>
                <MenuItem value="Create">Create</MenuItem>
                <MenuItem value="Update">Update</MenuItem>
                <MenuItem value="Delete">Delete</MenuItem>
                <MenuItem value="Payment">Payment</MenuItem>
              </Select>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  className="form_control"
                  slots={{
                    openPickerIcon: () => (
                      <img src="/static/images/date_icon.svg" alt="Pick date" />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      id: "dob",
                      placeholder: "Select Date",
                      fullWidth: true,
                      hiddenLabel: true,
                    },
                  }}
                  desktopModeMediaQuery="(min-width:0px)"
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow hover>
                <TableCell>Date/Time</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>IP Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div className="loader"></div>
                      <span style={{ marginLeft: "10px" }}>Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : activityLogs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    <div style={{ color: "#666", fontSize: "16px" }}>
                      <img
                        src="/static/images/placeholder.png"
                        alt="No data"
                        style={{
                          width: "64px",
                          height: "64px",
                          marginBottom: "16px",
                          opacity: 0.5,
                        }}
                      />
                      <p>No activity logs found</p>
                      <p style={{ fontSize: "14px", marginTop: "8px" }}>
                        Activity logs will appear here once users start
                        performing actions
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                activityLogs.map((row) => (
                  <TableRow hover key={row?._id}>
                    <TableCell>{formatDateTime(row?.createdAt)}</TableCell>
                    <TableCell>
                      <div className="user_block">
                        <p>
                          <b>{row?.userId?.email || "N/A"}</b>
                          <span>
                            {row?.userId?.userType
                              ? `User Type: ${row?.userId.userType}`
                              : ""}
                          </span>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{getModuleName(row?.module)}</TableCell>
                    <TableCell>
                      {getActivityTypeName(row?.activityType)}
                    </TableCell>
                    <TableCell className="max_c2">{row?.details}</TableCell>
                    <TableCell>{row?.ipAddress || "N/A"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 0 && (
          <Pagination
            module={activityLogs || []}
            page={page}
            setPage={setPage}
            limit={pageSize}
            setLimit={setPageSize}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        )}
      </div>
    </>
  );
};

export default ManageActivityLogs;
